import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { AUTH_ROUTE, REFRESH_ROUTE } from '@/routes';

export const getAuthToken = (): string | null => {
  return localStorage.getItem('AUTH_TOKEN');
};

export const setAuthData = (token: string, expiryDateTimestampInSeconds: number) => {
  localStorage.setItem('AUTH_TOKEN', `Bearer ${token}`);
  const expiresAt = expiryDateTimestampInSeconds * 1000;
  localStorage.setItem('AUTH_TOKEN_EXPIRES_AT', expiresAt.toString());
};

export const getAuthTokenExpiresAt = (): number | null => {
  const expiresAtString = localStorage.getItem('AUTH_TOKEN_EXPIRES_AT');
  return expiresAtString ? parseInt(expiresAtString, 10) : null;
};

export const clearAuthTokenData = () => {
  localStorage.removeItem('AUTH_TOKEN');
  localStorage.removeItem('AUTH_TOKEN_EXPIRES_AT');
};

export const logout = () => {
  clearAuthTokenData();
  window.location.href = AUTH_ROUTE;
};

export const setUsername = (username: string) => {
  localStorage.setItem('USERNAME', username);
};

export const getUsername = (): string | null => {
  return localStorage.getItem('USERNAME');
};

export const clearUsername = () => {
  localStorage.removeItem('USERNAME');
};

const baseURL = window.API_URL;

export const axiosInstance = axios.create({
  baseURL,
});

// --- Token Refresh Logic ---
let isRefreshing = false;
// Queue for requests waiting for token refresh
let failedQueue: Array<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve: any;
  reject: (error: Error | AxiosError) => void;
  config: InternalAxiosRequestConfig;
}> = [];

const processQueue = (error: Error | AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      if (prom.config.headers) {
        prom.config.headers.Authorization = `Bearer ${token}`;
      }
      prom.config.withCredentials = true;
      // Retry the request with the new token using the original axiosInstance
      axiosInstance(prom.config)
        .then((response) => prom.resolve(response))
        .catch((err: Error | AxiosError) => prom.reject(err));
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    const expiresAt = getAuthTokenExpiresAt();
    const now = new Date().getTime();
    const bufferMilliseconds = 60 * 1000; // 60 seconds buffer (refresh 1 min before actual expiry)

    // Do not attempt to refresh for the refresh token endpoint itself or if no token/expiry
    const refreshUrl = `/auth${REFRESH_ROUTE}`;
    if (!token || !expiresAt) {
      if (token && !config.headers.Authorization) config.headers.Authorization = token;
      return config;
    }

    if (now >= expiresAt - bufferMilliseconds) {
      // Token is expired or about to expire
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // axios.post is used to avoid interceptor loop
          const response = await axios.post(
            `${axiosInstance.defaults.baseURL}${refreshUrl}`,
            {},
            { withCredentials: true },
          );

          const newAccessToken = response.data.token;
          const newExpiresIn = response.data.expires;

          if (!newAccessToken || typeof newExpiresIn !== 'number') {
            throw new Error('Invalid token refresh response from server.');
          }

          setAuthData(newAccessToken, newExpiresIn);
          config.headers.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          return config; // Continue with the original request, now with the new token
        } catch (refreshError) {
          processQueue(refreshError as Error | AxiosError, null); // Reject queued requests
          logout(); // Logout if refresh fails (e.g., refresh token expired -> 401 from /refresh)
          return Promise.reject(refreshError); // Halt the original request
        } finally {
          isRefreshing = false;
        }
      } else {
        // Token is currently refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config });
        });
      }
    } else {
      // Token is valid and not expiring soon
      config.headers.Authorization = token;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const statusCode = error.response?.status;
    const originalRequest = error.config;
    if (
      (statusCode === 401 || statusCode === 403) &&
      originalRequest &&
      originalRequest.url !== `${axiosInstance.defaults.baseURL}${REFRESH_ROUTE}` &&
      !window.location.pathname.includes(AUTH_ROUTE)
    ) {
      if (!isRefreshing) {
        // Avoid logout if a refresh is already trying to fix an auth issue
        logout();
      }
    } else {
      return Promise.reject(error);
    }
  },
);
